/**
 * Sidebar — folder list and primary navigation. Becomes a slide-out drawer
 * on mobile, persistent on desktop. Each folder can be renamed, recolored, or
 * deleted inline via its ⋮ button.
 */

import { useState } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  Archive,
  Bell,
  Check,
  Folder as FolderIcon,
  FolderPlus,
  Lightbulb,
  MoreVertical,
  Plus,
  Trash2,
} from "lucide-react";
import { useCreateFolder, useDeleteFolder, useFolders, useUpdateFolder } from "../api/hooks";
import type { Folder } from "../api/types";
import { useToast } from "./toast";
import { useConfirm } from "./confirm";

interface SidebarProps {
  onNavigate?: () => void;
}

// Saturated accents for the folder icon, keyed by the same color tokens notes
// use. "default" (or null) renders a muted, uncolored folder.
const FOLDER_COLORS: Record<string, string> = {
  red: "#ef4444",
  orange: "#f97316",
  yellow: "#eab308",
  green: "#22c55e",
  teal: "#14b8a6",
  blue: "#3b82f6",
  purple: "#a855f7",
  pink: "#ec4899",
  brown: "#b45309",
  gray: "#6b7280",
};
const COLOR_TOKENS = ["default", ...Object.keys(FOLDER_COLORS)];

function folderAccent(color: string | null | undefined): string {
  return (color && FOLDER_COLORS[color]) || "var(--fg-muted)";
}

export function Sidebar({ onNavigate }: SidebarProps) {
  const foldersQ = useFolders();
  const create = useCreateFolder();
  const route = useRouterState({ select: (s) => s.location.pathname });
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");

  function link(to: string, label: string, Icon: typeof Lightbulb) {
    const active = route === to;
    return (
      <Link
        to={to}
        onClick={onNavigate}
        className={`flex items-center gap-2 px-3 py-2 rounded text-sm ${
          active ? "font-semibold" : ""
        }`}
        style={{
          background: active ? "var(--selected)" : "transparent",
          color: "var(--fg)",
        }}
      >
        {/* Label wrapped in a <span> so the icon isn't the only ELEMENT child —
         * otherwise the coarse-pointer touch rule (svg:only-child, which ignores
         * the text node) would center & shrink these rows on mobile. */}
        <Icon
          className="w-4 h-4 shrink-0"
          style={active ? { color: "var(--color-primary-dark)" } : undefined}
        />
        <span>{label}</span>
      </Link>
    );
  }

  return (
    <nav className="w-full h-full p-2 flex flex-col gap-1 overflow-y-auto">
      {link("/", "Notes", Lightbulb)}
      {link("/reminders", "Reminders", Bell)}
      {link("/archive", "Archive", Archive)}
      {link("/trash", "Trash", Trash2)}

      <div
        className="mt-4 mb-1 px-3 text-xs uppercase tracking-wide"
        style={{ color: "var(--fg-muted)" }}
      >
        Folders
      </div>
      {(foldersQ.data ?? []).map((f) => (
        <FolderRow
          key={f.id}
          folder={f}
          active={route === `/folders/${f.id}`}
          onNavigate={onNavigate}
        />
      ))}

      {adding ? (
        <form
          className="flex gap-1 mt-1 px-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!newName.trim()) return;
            create.mutate(
              { name: newName.trim() },
              {
                onSuccess: () => {
                  setNewName("");
                  setAdding(false);
                },
              }
            );
          }}
        >
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Folder name"
            className="flex-1 px-2 py-1 rounded border text-sm"
            style={{ borderColor: "var(--border)", background: "var(--bg)" }}
          />
          <button
            type="submit"
            className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5"
            aria-label="Save folder"
            title="Save folder"
          >
            <Plus className="w-4 h-4" />
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex items-center gap-2 px-3 py-2 rounded text-sm hover:bg-black/5 dark:hover:bg-white/5"
        >
          <FolderPlus className="w-4 h-4 shrink-0" /> <span>New folder</span>
        </button>
      )}

      {/* Build version — desktop sidebar only, pinned to the bottom. Shows the
       * release tag on prod builds, or the commit hash (+ -dirty) on test/dev. */}
      <div
        className="mt-auto hidden lg:block px-3 pt-3 pb-1 text-[11px] tabular-nums select-none"
        style={{ color: "var(--fg-muted)", opacity: 0.7 }}
        title={`keep ${__APP_VERSION__}`}
      >
        {__APP_VERSION__}
      </div>
    </nav>
  );
}

function FolderRow({
  folder,
  active,
  onNavigate,
}: {
  folder: Folder;
  active: boolean;
  onNavigate?: () => void;
}) {
  const update = useUpdateFolder();
  const del = useDeleteFolder();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(folder.name);
  const [color, setColor] = useState<string>(folder.color ?? "default");

  function open() {
    setName(folder.name);
    setColor(folder.color ?? "default");
    setEditing(true);
  }

  function save() {
    const trimmed = name.trim();
    if (!trimmed) return;
    update.mutate(
      { id: folder.id, name: trimmed, color: color === "default" ? null : color },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => toast.push((err as Error).message, "error"),
      }
    );
  }

  async function remove() {
    const ok = await confirm({
      title: `Delete folder "${folder.name}"?`,
      message: "Its notes stay — they're just un-filed.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    del.mutate(folder.id, {
      onSuccess: () => {
        setEditing(false);
        if (active) navigate({ to: "/" });
      },
      onError: (err) => toast.push((err as Error).message, "error"),
    });
  }

  if (editing) {
    return (
      <div
        className="mx-1 my-1 p-2 rounded border flex flex-col gap-2"
        style={{ borderColor: "var(--border)", background: "var(--bg-elevated)" }}
      >
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            else if (e.key === "Escape") setEditing(false);
          }}
          aria-label="Folder name"
          className="px-2 py-1 rounded border text-sm"
          style={{ borderColor: "var(--border)", background: "var(--bg)" }}
        />
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Folder color">
          {COLOR_TOKENS.map((tok) => {
            const selected = color === tok;
            return (
              <button
                key={tok}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={tok}
                title={tok}
                onClick={() => setColor(tok)}
                className="w-9 h-9 rounded-full border flex items-center justify-center shrink-0"
                style={{
                  background: tok === "default" ? "var(--bg)" : FOLDER_COLORS[tok],
                  borderColor: selected ? "var(--fg)" : "var(--border)",
                  borderWidth: selected ? 2 : 1,
                }}
              >
                {tok === "default" && !selected && (
                  <FolderIcon className="w-4 h-4" style={{ color: "var(--fg-muted)" }} />
                )}
                {selected && (
                  <Check
                    className="w-4 h-4"
                    style={{ color: tok === "default" ? "var(--fg)" : "#fff" }}
                  />
                )}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={remove}
            className="text-xs font-medium text-red-600 inline-flex items-center gap-1"
          >
            <Trash2 className="w-3.5 h-3.5 shrink-0" /> <span>Delete</span>
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="text-xs px-2 py-1 rounded border"
            style={{ borderColor: "var(--border)" }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!name.trim() || update.isPending}
            className="text-xs px-2 py-1 rounded bg-amber-400 text-black font-medium"
          >
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="group flex items-center gap-1 pr-1 rounded text-sm"
      style={{ background: active ? "var(--selected)" : "transparent" }}
    >
      <Link
        to="/folders/$id"
        params={{ id: folder.id }}
        onClick={onNavigate}
        className="flex items-center gap-2 flex-1 min-w-0 px-3 py-2"
        style={{ color: "var(--fg)" }}
      >
        <FolderIcon className="w-4 h-4 shrink-0" style={{ color: folderAccent(folder.color) }} />
        <span className="truncate">{folder.name}</span>
      </Link>
      <span className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
        {folder.note_count ?? 0}
      </span>
      <button
        type="button"
        onClick={open}
        aria-label={`Edit folder ${folder.name}`}
        title="Edit folder"
        className="p-1 rounded opacity-60 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/5 shrink-0"
      >
        <MoreVertical className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
